import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";

const DownloadStatementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const today = new Date();

    const twoMonthsAgo = new Date(today);
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    setFromDate(formatDate(twoMonthsAgo));
    setToDate(formatDate(today));
  }, []);

  // Retrieve details from navigation state or fallback to localStorage
  const { sageAccountNumber, gmaAccountNumberID, userEmail, userProfileID } =
    location.state || {
      sageAccountNumber: localStorage.getItem("sageAccountNumber"),
      gmaAccountNumberID: localStorage.getItem("gmaAccountNumberId"),
      userEmail: localStorage.getItem("userEmail"),
      userProfileID: localStorage.getItem("userProfileId"),
    };

  // New state for user access levels for the navbar dropdown
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  useEffect(() => {
    document.title = "GMA Portal || Download Statement";
    if (!gmaAccountNumberID || !userProfileID || !userEmail) {
      setError(
        "Missing required account details. Please go back and try again." +
          sageAccountNumber +
          " " +
          gmaAccountNumberID +
          " " +
          userEmail +
          " " +
          userProfileID,
      );
      setLoading(false); // ✅ Ensure loading stops
      localStorage.removeItem("token");
      navigate("/");
      return;
    }
    setLoading(false);
  }, [
    gmaAccountNumberID,
    userProfileID,
    userEmail,
    sageAccountNumber,
    navigate,
  ]);

  // New useEffect to fetch user access levels
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        const email = decodedToken.email || null;
        const contact_id = decodedToken.contact_id || null;

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const response = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          },
        );

        const data = await response.json();
        if (response.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        // silent
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  const [statementFormat, setStatementFormat] = useState("pdf");
  const [emailMode, setEmailMode] = useState("profile");
  const [customEmail, setCustomEmail] = useState("");
  const [loadingType, setLoadingType] = useState(null);

  const handleStatement = async (delivery, event) => {
    event.preventDefault();

    if (!toDate) {
      setError("Please select a To Date.");
      return;
    }

    if (fromDate && new Date(fromDate) > new Date(toDate)) {
      setError("Invalid date range.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const destinationEmail =
      emailMode === "custom" ? customEmail.trim() : userEmail;

    if (delivery === "email" && !destinationEmail) {
      setError("Please select or enter an email address.");
      return;
    }

    setError("");
    setSuccessMessage("");
    setLoadingType(`${delivery}-${statementFormat}`);

    try {
      const response = await fetch(
        `https://portal.gmacc.co.za/api/account/${gmaAccountNumberID}/statement/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            delivery,
            format: statementFormat,
            from: fromDate || "1900-01-01",
            to: toDate,
            email: delivery === "email" ? destinationEmail : "",
          }),
        },
      );

      if (!response.ok) throw new Error(await response.text());

      if (delivery === "download") {
        const blob = await response.blob();
        const ext = statementFormat === "excel" ? "xlsx" : "pdf";
        const url = window.URL.createObjectURL(blob);

        const contentDisposition =
          response.headers.get("Content-Disposition") || "";
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);

        const safeName = (sageAccountNumber || "account").replace(
          /[^a-zA-Z0-9-_]/g,
          "_",
        );

        const filename =
          match?.[1] ||
          `Statement_${safeName}_${new Date().toISOString().slice(0, 10)}.${ext}`;

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);
        setSuccessMessage("Statement downloaded.");
      } else {
        setSuccessMessage(`Statement emailed to ${destinationEmail}.`);
      }
    } catch (err) {
      setError(err.message || "Statement request failed.");
    } finally {
      setLoadingType(null);
    }
  };

  const handleTrusteePortalClick = () => {
    navigate("/trustee-portal");
  };
  const handleContractorPortalClick = () => {
    navigate("/contractor-portal");
  };

  return (
    <div className={styles.page}>
      {/* main 50/50 layout like the screenshot */}
      <div className={styles.authFrame}>
        <div className={styles.authMedia} aria-hidden="true"></div>
        <div className={styles.authPanel}>
          {loading ? (
            <div className={styles.page}>
              <div className={styles.shell}>
                <main className={styles.contentArea}>
                  <div className={styles.panel}>
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingImage}></div>
                      <p className={styles.loadingText}>Loading...</p>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          ) : (
            <div className={styles.formContainer}>
              <img
                src={logoimage}
                alt="Gauteng Management Agents"
                className={styles.logoImage}
              />
              <h2 className={styles.heading}>
                STATEMENT DOWNLOAD {sageAccountNumber}
              </h2>
              <p className={styles.subhead}>Select Date Range.</p>
              <p className={styles.subheadChoose}>
                Choose the date range for the statement you wish to download.
              </p>

              {error && <p className={styles.errorText}>{error}</p>}
              {successMessage && (
                <p className={styles.successText}>{successMessage}</p>
              )}

              <form className={styles.statementForm}>
                <div className={styles.dateRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.subhead}>From Date Optional</label>
                    <div className={styles.inputShell}>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={styles.input}
                      />
                    </div>
                  </div>

                  <div className={styles.inputGroup}>
                    <label className={styles.subhead}>To Date</label>
                    <div className={styles.inputShell}>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={styles.input}
                      />
                    </div>
                  </div>
                </div>

                <div className={styles.dateRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.subhead}>File Type</label>
                    <select
                      className={styles.input}
                      value={statementFormat}
                      onChange={(e) => setStatementFormat(e.target.value)}
                    >
                      <option value="pdf">PDF</option>
                      <option value="excel">Excel</option>
                    </select>
                  </div>
                </div>

                <div className={styles.row}>
                  <button
                    className={styles.portalButton}
                    onClick={(e) => handleStatement("download", e)}
                    disabled={!!loadingType}
                  >
                    {loadingType?.startsWith("download")
                      ? "Downloading..."
                      : "Download Statement"}
                  </button>
                </div>

                <div className={styles.backLink} onClick={() => navigate(-1)}>
                  Back
                </div>

                <div
                  className={styles.backLink}
                  onClick={() => navigate("/#/")}
                >
                  Log Out
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadStatementPage;
